import { Customer } from 'Services/Mongo/Types';
import {
  Product,
  ProductColumn,
  ProductColumnsIds
} from '@setvi/shared/interfaces';
import { EditorProduct } from '@setvi/shared/components/seditor/interfaces';

export { SINGER_FACET_LABEL_OVERRIDES } from '@setvi/shared/utils/singer-labels';

const removeWords = ['Custom'];

export const getProductStock = (inventory: Product['inventory']): number =>
  inventory?.reduce((acc, curr) => acc + (curr?.availableOnHand || 0), 0) || 0;

export const getProductStockByCustomer = (
  inventory: Product['inventory'],
  customer: Customer
) =>
  inventory?.find(
    // eslint-disable-next-line no-underscore-dangle
    warehouse => warehouse.warehouseId === customer?.warehouse?.code
  )?.availableOnHand || 0;

export const parseProductsToEmailProduct = (
  products: Product[],
  columns?: ProductColumn[]
): EditorProduct[] => {
  const ids = columns ? new Set(columns.map(c => c.id)) : null;
  const hasPrice =
    !ids ||
    ids.has(ProductColumnsIds.Price) ||
    ids.has(ProductColumnsIds.ListPrice) ||
    ids.has(ProductColumnsIds.GetPrice);
  const hasManufacturer = !ids || ids.has(ProductColumnsIds.Manufacturer);

  return products.map(product => ({
    productName: product.name,
    sku: product.sku,
    ...(hasPrice ? { price: product.price } : {}),
    // @ts-ignore
    productLink: product.custom.ecommerceLink?.value,
    imageUrl: product.imageUrl || product.images[0]?.path,
    ...(hasManufacturer
      ? {
          vendorName: product?.vendor?.name,
          manufacturerSku: product?.manufacturerSku
        }
      : {})
  }));
};

export const getInStock = (
  inventory: Product['inventory'],
  customer: Customer
) => {
  const productStock = getProductStock(inventory);
  const stockForCustomer = getProductStockByCustomer(inventory, customer);
  const stock = customer ? stockForCustomer : productStock;

  return !!stock;
};

export const LIMIT_SELECTED_PRODUCTS = 50;
export const getNumOfProductsInComposeMail = (values: any) => {
  const parser = new DOMParser();

  const parsedHTML = parser.parseFromString(values.body, 'text/html');

  const selectedProductsList =
    parsedHTML.getElementsByClassName('selected-product');

  return selectedProductsList.length;
};

export const transformAlgoliaFacetLabel = (label: string) => {
  // Splits label on capital letters, dots and underscores
  const words = label.split(/(?=[A-Z])|\.|_/);

  const filteredWords = words.filter(
    i => !removeWords.some(j => j.toLocaleLowerCase() === i.toLocaleLowerCase())
  );

  // Capitalizes first letter of each word and lowercases the rest and adds a space between words
  const transformedLabel = filteredWords
    .map(
      word =>
        (word[0] || '').toUpperCase() + (word.slice(1) || '').toLowerCase()
    )
    .join(' ')
    .trim();
  return transformedLabel;
};

export enum ProductActionType {
  DetailsView = 'ProductDetails',
  AssetView = 'View Asset',
  AddProductToEmail = 'Add Products to Email',
  Search = 'Search',
  CustomerSelection = 'Customer Selection',
  CategorySelection = 'Category Selection',
  FiltersSelection = 'Filters Selection'
}

export const valueToMonetary = (
  value: number,
  options?: { [key: string]: string }
) =>
  value > 0 && Number.isFinite(value)
    ? `${value.toLocaleString('en-US', {
        currency: 'USD',
        style: 'currency',
        ...options
      })}`
    : '-';

export const parseKeyValueArrayToObject = (inputArray: string[]) => {
  const result: Record<string, string | string[]> = {};

  inputArray.forEach(entry => {
    const [key, ...rest] = entry.split(':');
    const rawValue = rest.join(':');

    const parsedValue: string = rawValue;

    if (Object.prototype.hasOwnProperty.call(result, key)) {
      if (Array.isArray(result[key])) {
        (result[key] as string[]).push(parsedValue);
      } else {
        result[key] = [result[key] as string, parsedValue];
      }
    } else {
      result[key] = parsedValue;
    }
  });

  return result;
};

export const appendPricingToProducts = (products: Product[], prices: any[]) =>
  products.map(item => {
    const obtainedPrice = prices?.find(i => i.sku === item.sku)?.price;
    const obtainedCost = prices?.find(i => i.sku === item.sku)?.cost;
    const obtainedIsEditable = prices?.find(
      i => i.sku === item.sku
      // @ts-ignore Incorrect response type on getPrices API
    )?.isEditable;

    return {
      ...item,
      ...(typeof obtainedPrice === 'number' ? { price: obtainedPrice } : {}),
      ...(typeof obtainedCost === 'number'
        ? { customerCost: obtainedCost }
        : {}),
      ...(typeof obtainedIsEditable === 'boolean'
        ? { isEditable: obtainedIsEditable }
        : {})
    };
  });
